import { useState, useRef, FormEvent } from "react";
import { useSearch } from "../../hooks/useSearch";
import { Box, Button, TextField, CircularProgress } from "@mui/material";
import { Product } from "../../pages/Home/Home";

function SearchBox({
  setSearchProducts,
  category,
}: {
  setSearchProducts: (products: number[] | null) => void;
  category: number | undefined;
}) {
  const [inputValue, setInputValue] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchClient = useSearch();

  const handleSearch = async (searchQuery: string) => {
    if (searchQuery.length === 0) {
      return;
    }

    setIsSearching(true);
    try {
      const searchOptions: any = {
        limit: 100,
      };

      if (category) {
        searchOptions.filter = `Categories = ${category}`;
      }

      const searchResults = await searchClient
        .index("stock")
        .search(`${searchQuery}`, searchOptions);

      const results = searchResults.hits as Product[];
      setSearchProducts(results.map((result) => result.ID));
    } catch (error) {
      console.error("Search error:", error);
      setSearchProducts(null);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (inputRef.current) {
      inputRef.current.blur();
    }

    handleSearch(inputValue);
  };

  const handleReset = (event: FormEvent) => {
    event.preventDefault();
    event.stopPropagation();

    setInputValue("");
    setSearchProducts(null);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      onReset={handleReset}
      sx={{ display: "flex", gap: 2, alignItems: "center" }}
    >
      <TextField
        inputRef={inputRef}
        fullWidth
        variant="outlined"
        size="small"
        placeholder="Search..."
        type="search"
        value={inputValue}
        onChange={(event) => {
          const newValue = event.target.value;
          setInputValue(newValue);
        }}
        InputProps={{
          endAdornment: isSearching && <CircularProgress size={20} />,
        }}
      />
      <Button
        type="submit"
        variant="contained"
        color="primary"
        disabled={inputValue.length === 0}
      >
        Search
      </Button>
      {inputValue.length > 0 && !isSearching && (
        <Button type="reset" variant="outlined">
          Clear
        </Button>
      )}
    </Box>
  );
}

export default function Search({
  setSearchProducts,
  category,
}: {
  setSearchProducts: (products: number[] | null) => void;
  category: number | undefined;
}) {
  return (
    <SearchBox setSearchProducts={setSearchProducts} category={category} />
  );
}
